import { Box, Flex, Image, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useContext, useEffect, useState } from 'react';
import { BrowserView, isMobile } from 'react-device-detect';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FilterIcon from '../assets/icons/FilterIcon.svg';
import FilterIconOff from '../assets/icons/FilterIconOff.svg';
import GridIcon from '../assets/icons/GridIcon.svg';
import ListIcon from '../assets/icons/ListIcon.svg';
import { isAuthenticated } from '../auth/keycloak';
import {
    DefaultFilterDrawer,
    FILTER_ALL,
    FILTER_BINGO,
    FILTER_LEVIATHAN,
    FILTER_MARBLES,
    FILTER_RDR,
} from '../component/gameComponents/DefaultFilterDrawer';
import { LiveSixListItem } from '../component/gameComponents/LiveSixListItem';
import { SearchInput } from '../component/gameComponents/SearchInput';
import { SixListItemCompact } from '../component/gameComponents/SixListItemCompact';
import { SortButtons, SortOption } from '../component/gameComponents/SortButtons';
import { BrowserSidebar } from '../component/globalComponents/BrowserSidebar';
import { BrowserTopBar } from '../component/globalComponents/BrowserTopBar';
import { MobileViewPage } from '../component/globalComponents/MobileViewPage';
import { BackIcon } from '../component/iconComponents/BackIcon';
import { CommonScrollList } from '../component/ScrollList';
import PageDataContext from '../context/PageDataContext';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { usePageDataCache } from '../hooks/usePageDataCache';
import { usePageSessionStorage } from '../hooks/usePageSessionStorage';
import {
    BINGO_LICENSE,
    LEVIATHAN_LICENSE,
    MARBLES_LICENSE,
    RACE_DAY_LICENSE,
} from '../service/games';
import {
    GameInfo,
    getRoomsByUserFilter,
    getRunningGames,
    PlatformOption,
    SortAttribute,
    SortDirection,
} from '../service/lobby';
import {
    getPublicProfiles,
    getPublicProfilesByUsername,
    getUserFollowing,
    UserProfile,
} from '../service/social';
import { PaginatedData } from '../service/util';
import { getLiveInfo } from './FollowingPage';

const LogPrefix = 'DiscoverPage';

export enum SortingOptions {
    NumberViewers = 'Number Of Viewers',
    NumPlayers = 'Number Of Players',
}

const toSortAttribute = (val: SortingOptions): SortAttribute => {
    switch (val) {
        case SortingOptions.NumPlayers:
            return SortAttribute.NumberPlayers;
        case SortingOptions.NumberViewers:
            return SortAttribute.NumViewers;
        default:
            throw new Error(`Invalid Sort Option ${val}`);
    }
};

export const DiscoverPage = () => {
    const toast = useToast();
    const FILTER_LICENSE_MAP: { [filter: string]: string } = {
        [FILTER_ALL]: '',
        [FILTER_MARBLES]: MARBLES_LICENSE,
        [FILTER_BINGO]: BINGO_LICENSE,
        [FILTER_RDR]: RACE_DAY_LICENSE,
        [FILTER_LEVIATHAN]: LEVIATHAN_LICENSE,
    };
    const LICENSE_FILTER_MAP: { [license: string]: string } = {
        '': FILTER_ALL,
        [MARBLES_LICENSE]: FILTER_MARBLES,
        [BINGO_LICENSE]: FILTER_BINGO,
        [RACE_DAY_LICENSE]: FILTER_RDR,
        [LEVIATHAN_LICENSE]: FILTER_LEVIATHAN,
    };

    const { keycloak } = useKeycloak();

    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();

    const { isLoadFromCache } = useContext(PageDataContext);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [allItems, setAllItems] = usePageSessionStorage<(GameInfo | UserProfile)[]>(
        'allItems',
        [],
    );
    const [filteredItems, setFilteredItems] = usePageSessionStorage<typeof allItems>(
        'filteredItems',
        [],
    );
    const [pagination, setPagination] = usePageDataCache<PaginatedData | null>('pagination', null);
    const [hasMore, setHasMore] = usePageDataCache<boolean>('hasMore', false);
    const [hasError, setHasError] = usePageDataCache<boolean>('hasError', false);
    const [gameTitleFilter, setGameTitleFilter] = usePageDataCache<string>(
        'gameTitleFilter',
        LICENSE_FILTER_MAP[searchParams.get('gameFilter') || ''],
    );
    const [streamingPlatformFilter, setStreamingPlatformFilter] = usePageDataCache<
        PlatformOption | undefined
    >('streamingPlatformFilter', undefined);
    const [searchText, setSearchText] = usePageDataCache<string>('searchText', '');
    const [profileMap, setProfileMap] = usePageDataCache<{ [id: string]: UserProfile }>(
        'profileMap',
        {},
    );
    const [view, setView] = useLocalStorage<'list' | 'grid'>('streamsix_view', 'grid');
    const [isFetchingData, setIsFetchingData] = useState<Array<boolean>>([]);
    const [abortController, setAbortController] = useState<AbortController>();
    const [isSearchingUser, setSearchingUser] = usePageDataCache<boolean>('isSearchingUser', false);
    const [following, setFollowing] = usePageDataCache<UserProfile[] | undefined>(
        'following',
        undefined,
    );
    const [followGameInfoMap, setFollowGameInfoMap] = usePageDataCache<GameInfoMap>(
        'followingGameInfoMap',
        {},
    );

    const [filteredGameInfoMap, setFilteredGameInfoMap] = usePageDataCache<GameInfoMap>(
        'filteredGameInfoMap',
        {},
    );
    type GameInfoMap = {
        [userId: string]: GameInfo;
    };

    const [filteredList, setFilteredList] = usePageDataCache<UserProfile[] | undefined>(
        'filteredList',
        undefined,
    );

    const [sortOption, setSortOption] = usePageSessionStorage('discover-sorting', {
        attribute: SortingOptions.NumberViewers,
        direction: SortDirection.DESC,
    });

    const [hasRunMountLogic, setHasRunMountLogic] = useState(false);

    const updateGameTitleFilter = (gameTitle: string) => {
        const logPrefix = `[${LogPrefix}.updateGameTitleFilter]`;
        setGameTitleFilter(gameTitle);
        console.log(`${logPrefix} set game title filter to: ${gameTitle}`);
    };

    const updateStreamingPlatformFilter = (platformString: string) => {
        const logPrefix = `[${LogPrefix}.updateStreamingPlatformFilter]`;
        const platform = platformString as PlatformOption;
        setStreamingPlatformFilter(
            Object.values(PlatformOption).includes(platform) ? platform : undefined,
        );
        console.log(`${logPrefix} set streaming platform filter to: ${platform}`);
    };

    const applyFilters = () => {
        onClose();

        const license = FILTER_LICENSE_MAP[gameTitleFilter];

        fetchFreshSessionData('', license, streamingPlatformFilter, sortOption);
    };

    const resetFilters = () => {
        setSearchingUser(false);
        setGameTitleFilter(FILTER_ALL);
        setStreamingPlatformFilter(undefined);
        setSortOption({ attribute: SortingOptions.NumberViewers, direction: SortDirection.DESC });
        onClose();

        const license = '';
        fetchFreshSessionData('', license, streamingPlatformFilter, sortOption);
    };

    const searchUsers = async (text: string, nextPage: string) => {
        const logPrefix = `[${LogPrefix}.searchUsers]`;

        try {
            if (isFetchingData.length > 0) {
                console.log(`${logPrefix} already fetching, skip request`);
                return;
            }

            const reset = nextPage === '';

            console.log(`${logPrefix} starting with reset: ${reset}`);

            setGameTitleFilter(FILTER_ALL);
            setStreamingPlatformFilter(undefined);
            setSearchingUser(true);

            isFetchingData.push(true);
            setIsFetchingData([...isFetchingData]);

            const newController = new AbortController();
            setAbortController(newController);

            // query profiles from social
            const searchResp = await getPublicProfilesByUsername(
                text,
                false,
                nextPage,
                newController.signal,
            );

            console.log(`${logPrefix} receive profiles: ${JSON.stringify(searchResp.profiles)}`);

            // query sessions from lobby
            const lobbySessions = await getRoomsByUserFilter(
                {
                    userFilter: {
                        streamsix: Object.entries(searchResp.profiles).map(([_, v]) => v.id),
                    },
                },
                newController.signal,
            );

            console.log(`${logPrefix} receive sessions: ${JSON.stringify(lobbySessions)}`);

            setHasMore(searchResp.page !== undefined && searchResp.page !== null);
            setPagination(searchResp.page || null);

            const compiledSessions = Object.entries(searchResp.profiles).map(([_, profile]) => {
                const session = lobbySessions?.find((x) => x.owner === profile.id);
                if (session) {
                    return session;
                }

                return profile;
            });

            if (reset) {
                setAllItems(compiledSessions);
            } else {
                const toAdd = compiledSessions;
                toAdd.forEach((x) => {
                    // scrub duplicate sessions
                    if (!allItems.find((y) => y.id === x.id)) {
                        allItems.push(x);
                    }
                });
                setAllItems(allItems);
            }

            if (reset) {
                setProfileMap({ ...searchResp.profiles });
            } else {
                setProfileMap((prevProfileMap) => {
                    return { ...prevProfileMap, ...searchResp.profiles };
                });
            }

            isFetchingData.pop();
            setIsFetchingData([...isFetchingData]);
            setHasError(false);
            console.log(`${logPrefix} done`);
        } catch (ex) {
            isFetchingData.pop();
            setIsFetchingData([...isFetchingData]);
            setHasError(true);
            setProfileMap({ ...profileMap });
            console.error(`${logPrefix} ${ex}`);
        }
    };

    const filterItems = (
        profileMap: Record<string, UserProfile>,
        allItems: (UserProfile | GameInfo)[],
    ): typeof allItems =>
        allItems.filter((g) =>
            isGameInfo(g) ? (profileMap[g.owner] || g.userProfile) !== undefined : true,
        );

    const listView = () => {
        setView('list');
    };

    const gridView = () => {
        setView('grid');
    };

    const getFilterIcon = () => {
        if (isOpen) {
            return FilterIcon;
        } else {
            return FilterIconOff;
        }
    };

    const fetchFreshSessionData = async (
        searchText: string,
        license: string,
        streamingPlatform: PlatformOption | undefined,
        sortOption: SortOption,
    ) => {
        abortController?.abort();
        setSearchText(searchText);
        setAllItems([]);
        setFilteredItems([]);
        setPagination(null);
        setHasMore(false);
        if (searchText.length >= 3) {
            searchUsers(searchText, '');
        } else {
            fetchSessionData(true, '', license, streamingPlatform, sortOption);
        }
    };

    const fetchMoreSessionData = async () => {
        if (isSearchingUser) {
            searchUsers(searchText, pagination?.next || '');
        } else {
            const nextPage = pagination?.next || '';
            const license = FILTER_LICENSE_MAP[gameTitleFilter];
            fetchSessionData(false, nextPage, license, streamingPlatformFilter, sortOption);
        }
    };

    const fetchSessionData = async (
        reset: boolean,
        nextPage: string,
        license: string,
        streamingPlatform: PlatformOption | undefined,
        sortOption: SortOption,
    ) => {
        const logPrefix = `[${LogPrefix}._fetchSessionData]`;

        if (isFetchingData.length > 0 && !reset) {
            console.log(`${logPrefix} already fetching, skip request`);
            return;
        }
        isFetchingData.push(true);
        setIsFetchingData([...isFetchingData]);

        const newController = new AbortController();
        setAbortController(newController);

        console.log(`${logPrefix} starting with reset: ${reset}`);

        try {
            // query sessions from lobby
            const sortAttribute = toSortAttribute(sortOption.attribute);
            const lobbySessions = await getRunningGames(
                [{ ...sortOption, attribute: sortAttribute }],
                license,
                streamingPlatform,
                nextPage,
            );
            console.log(`${logPrefix} receive: ${JSON.stringify(lobbySessions)}`);

            setHasMore(!!lobbySessions?.page?.next);
            setPagination(lobbySessions?.page || null);

            if (reset) {
                setAllItems(lobbySessions?.data || []);
                setProfileMap({});
            } else {
                const toAdd = lobbySessions?.data || [];
                toAdd.forEach((x) => {
                    // scrub duplicate sessions
                    if (!allItems.find((y) => y.id === x.id)) {
                        allItems.push(x);
                    }
                });
                setAllItems(allItems);
            }

            updateProfiles(lobbySessions?.data || []);

            isFetchingData.pop();
            setIsFetchingData([...isFetchingData]);
            setHasError(false);
            console.log(`${logPrefix} done with reset: ${reset}`);
        } catch (ex) {
            isFetchingData.pop();
            setIsFetchingData([...isFetchingData]);
            setHasError(true);
            setProfileMap({ ...profileMap });
            console.error(`${logPrefix} ${ex}`);
        }
    };

    const fetchFollowingInformation = async () => {
        if (!isAuthenticated(keycloak)) {
            return;
        }

        try {
            let following = await getUserFollowing(keycloak.subject, keycloak.token);
            const { profiles, gameInfoMap } = await getLiveInfo(following);

            setFollowing(profiles);
            setFollowGameInfoMap(gameInfoMap);

            setFilteredGameInfoMap(gameInfoMap);
            setFilteredList(profiles);
        } catch (e) {
            toast({
                title: 'FOLLOWING - Something Went Wrong',
                description: (e as Error).message,
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        }
    };

    const hasMoreData = (hasMore: boolean): boolean => {
        const logPrefix = `[${LogPrefix}.hasMoreData]`;
        console.log(`${logPrefix} hasMore: ${hasMore} isFetchingData: ${isFetchingData.length}`);
        return hasMore || isFetchingData.length > 0;
    };

    const isGameInfo = (listItem: GameInfo | UserProfile): listItem is GameInfo => {
        return (listItem as GameInfo).sessionId !== undefined;
    };

    const updateProfiles = async (gameData: GameInfo[]) => {
        const owners = gameData.map((data) => data.owner);

        const profiles = await getPublicProfiles(owners);
        setProfileMap((prevProfileMap) => {
            return { ...prevProfileMap, ...profiles };
        });
    };

    useEffect(() => {
        fetchFollowingInformation();
        if (!isLoadFromCache) {
            const search = '';
            let license = FILTER_LICENSE_MAP[gameTitleFilter];
            if (searchParams.has('gameFilter')) {
                license = searchParams.get('gameFilter') || FILTER_LICENSE_MAP[gameTitleFilter];
                setSearchParams((params) => {
                    params.delete('gameFilter');
                    return new URLSearchParams(params);
                });
            }
            fetchFreshSessionData(search, license, streamingPlatformFilter, sortOption);
        }
        setHasRunMountLogic(true);
    }, []);

    useEffect(() => {
        // once we have complete profile map, filter out guest players
        const filtered = filterItems(profileMap, allItems);
        setFilteredItems(filtered);
    }, [profileMap, allItems]);

    useEffect(() => {
        if (hasRunMountLogic) {
            applyFilters();
        }
    }, [sortOption]);

    const renderDiscoverContent = (justifyContent: string) => (
        <CommonScrollList
            loading={isFetchingData.length > 0}
            onLoadMore={() => {
                fetchMoreSessionData();
            }}
            hasMore={hasMoreData(hasMore)}
            hasError={hasError}
            onRefresh={async () => {
                const license = FILTER_LICENSE_MAP[gameTitleFilter];
                fetchFreshSessionData(searchText, license, streamingPlatformFilter, sortOption);
            }}
        >
            {view === 'grid' ? (
                <Flex
                    className={
                        isMobile
                            ? 'current-six-flex-container mobile-flex-adjust'
                            : 'current-six-flex-container'
                    }
                    justifyContent={justifyContent}
                >
                    {filteredItems.map((listItem, idx) => (
                        <LiveSixListItem
                            gameInfo={isGameInfo(listItem) ? listItem : undefined}
                            profile={isGameInfo(listItem) ? profileMap[listItem.owner] : listItem}
                            index={idx}
                            displayNotifications={false}
                            following={following}
                            key={listItem.id}
                        />
                    ))}
                </Flex>
            ) : (
                <Flex className="compact-flex-container">
                    {filteredItems.map((listItem, idx) => (
                        <SixListItemCompact
                            key={listItem.id}
                            gameInfo={isGameInfo(listItem) ? listItem : undefined}
                            profile={isGameInfo(listItem) ? profileMap[listItem.owner] : listItem}
                            index={idx}
                            displayNotifications={false}
                            following={following}
                        />
                    ))}
                </Flex>
            )}
        </CommonScrollList>
    );

    return (
        <>
            <BrowserView>
                <BrowserTopBar isLoading={isFetchingData.length > 0} loadingColor={'#6ef1d5'}>
                    <Flex className={'discover-header flex-expand'}>
                        <Flex></Flex>
                        <Flex className={'header'}>
                            <Text>
                                <FormattedMessage id="discover.title" defaultMessage={'DISCOVER'} />
                            </Text>
                        </Flex>
                        <Flex className={'header-options'}>
                            <SearchInput
                                view={'browser'}
                                onSearchClear={resetFilters}
                                onTriggerSearch={(searchText) => {
                                    const license = '';
                                    fetchFreshSessionData(
                                        searchText,
                                        license,
                                        streamingPlatformFilter,
                                        sortOption,
                                    );
                                }}
                            />
                            <Image src={getFilterIcon()} onClick={onOpen} />
                            <Image src={ListIcon} onClick={listView} />
                            <Image src={GridIcon} onClick={gridView} />
                        </Flex>
                    </Flex>
                </BrowserTopBar>
                <BrowserSidebar />
                <Box className={'discover-container-browser browser-content'}>
                    <SortButtons
                        disabled={!hasRunMountLogic}
                        searchText={searchText}
                        sortOption={sortOption}
                        setSortOption={setSortOption}
                    />
                    {renderDiscoverContent('left')}
                    <DefaultFilterDrawer
                        onClose={onClose}
                        isOpen={isOpen}
                        resetFilters={resetFilters}
                        applyFilters={applyFilters}
                        updateGameTitleFilter={updateGameTitleFilter}
                        gameTitleFilter={gameTitleFilter}
                        updateStreamingPlatformFilter={updateStreamingPlatformFilter}
                        streamingPlatformFilter={streamingPlatformFilter || FILTER_ALL}
                    />
                </Box>
            </BrowserView>
            <MobileViewPage
                title="DISCOVER"
                titleColor="#000"
                titleBackgroundColor="#2ee5be"
                customBanner={
                    <Flex className={'discover-header'}>
                        <Flex onClick={() => navigate(-1)}>
                            <BackIcon />
                        </Flex>
                        <Flex className={'header'}>
                            <Text>
                                <FormattedMessage id="discover.title" defaultMessage={'DISCOVER'} />
                            </Text>
                        </Flex>
                        <Flex className={'header-options'}>
                            <Image src={getFilterIcon()} onClick={onOpen} />
                            <Image src={ListIcon} onClick={listView} />
                            <Image src={GridIcon} onClick={gridView} />
                        </Flex>
                    </Flex>
                }
                isLoading={isFetchingData.length > 0}
            >
                <Flex className={'mobile-space-above-infinite-scroll'}>
                    <SortButtons
                        disabled={!hasRunMountLogic}
                        searchText={searchText}
                        sortOption={sortOption}
                        setSortOption={setSortOption}
                    />
                    <SearchInput
                        view={'mobile'}
                        onSearchClear={resetFilters}
                        onTriggerSearch={(searchText) => {
                            fetchFreshSessionData(
                                searchText,
                                gameTitleFilter,
                                streamingPlatformFilter,
                                sortOption,
                            );
                        }}
                    />
                </Flex>
                {/* Hosts Section */}
                <Flex id={'discover-scroll'} className={'discover-content-mobile'}>
                    {renderDiscoverContent('center')}
                </Flex>
                <DefaultFilterDrawer
                    onClose={onClose}
                    isOpen={isOpen}
                    resetFilters={resetFilters}
                    applyFilters={applyFilters}
                    updateGameTitleFilter={updateGameTitleFilter}
                    gameTitleFilter={gameTitleFilter}
                    updateStreamingPlatformFilter={updateStreamingPlatformFilter}
                    streamingPlatformFilter={streamingPlatformFilter || FILTER_ALL}
                />
            </MobileViewPage>
        </>
    );
};
